import { Container, Graphics } from 'pixi.js';
import type { RoomContext, RoomDecorator } from './index';
import { place } from './index';
import { registerScenery, synthCtx } from '../scenery';

/**
 * STUDIO — a bright creative team office (Floorplan v2 / ADR-0022), in the slot the toilet used to hold.
 * On-theme for a "virtual AAA studio": a concept-art PIN WALL of sketches along the north wall, a DESIGN
 * DESK with dual monitors as the hero, a cosy reading/ideation NOOK (armchair + side table) tucked in the
 * SE corner, an art-book shelf on the east wall, ONE neat rug sized to the desk group, and exactly two
 * corner plants warming it. Order + style pass (Floorplan v2 polish): the old oversized centred rug and
 * the scattered greenery were cut; every seat here is sittable.
 *
 * Geometry (zone x[1120..1560] y[580..840], centre 1340,710):
 *   - the ONLY door is studio-W on the WEST wall → opening y[640..780] (in from the open-space hub);
 *   - so the LEFT strip (x<~1215) in that band stays an open walk-in lane; all furniture lives to the
 *     right of it and along the N/E/S walls.
 *
 * SEAT ANCHORS (this room's two sittable spots — register these in `world/seats.ts` to make them live;
 * the chair geometry below is placed + rotated to match each anchor's facing so a seated duck lines up):
 *   - studio-desk-chair : (1360, 706) facing -π/2 (UP, toward the dual monitors)  kind 'chair'
 *   - studio-nook-chair : (1502, 798) facing -π/2 (UP/inward, into the reading nook) kind 'chair'
 *
 * Built ONCE on map build/re-skin — no animation, no per-frame work.
 */

/** A concept-art PIN WALL: a framed cork board with pinned sketch cards + a couple of pin dots. */
function pinWall(ctx: RoomContext, w: number, h: number): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const frame = darken(art.wall, 0.18);
  const cork = mix(0xb98b54, art.wall, 0.3);
  const g = new Graphics()
    .roundRect(-w / 2 - 3, -h / 2 - 3, w + 6, h + 6, 3)
    .fill(frame)
    .roundRect(-w / 2, -h / 2, w, h, 2)
    .fill(cork)
    .roundRect(-w / 2, -h / 2, w, h * 0.4, 2)
    .fill({ color: 0xffffff, alpha: 0.05 });
  // pinned sketch cards in two rows, lightly rotated for a hand-pinned feel
  const cardW = 26;
  const cardH = 20;
  const cols = Math.max(1, Math.floor((w - 12) / (cardW + 8)));
  const palette = [0xf2efe6, 0xe8eef5, 0xf5ece8, 0xeaf2ea];
  let i = 0;
  for (const ry of [-h / 4, h / 5]) {
    for (let cidx = 0; cidx < cols; cidx++) {
      const cxp = -w / 2 + 12 + cidx * (cardW + 8);
      const tilt = ((i % 3) - 1) * 1.4;
      g.roundRect(cxp, ry - cardH / 2 + tilt, cardW, cardH, 1.5)
        .fill({ color: palette[i % palette.length]!, alpha: 0.95 })
        // a couple of sketch strokes on the card
        .moveTo(cxp + 4, ry - 3 + tilt)
        .lineTo(cxp + cardW - 5, ry - 3 + tilt)
        .moveTo(cxp + 4, ry + 3 + tilt)
        .lineTo(cxp + cardW - 10, ry + 3 + tilt)
        .stroke({ color: darken(art.accent, 0.1), width: 1, alpha: 0.5 })
        // the pin
        .circle(cxp + cardW / 2, ry - cardH / 2 + tilt, 1.6)
        .fill(lighten(art.accent, 0.15));
      i++;
    }
  }
  c.addChild(g);
  return c;
}

/** A DESIGN DESK: a wide top on two legs with a dual-monitor setup, a tablet/keyboard, and a pen cup. */
function designDesk(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const top = mix(0x7a5733, art.wall, 0.22);
  const steel = mix(0x9aa1ab, art.wall, 0.18);
  const screen = darken(art.windowSky, 0.25);
  c.addChild(new Graphics().roundRect(-62, 6, 124, 16, 4).fill({ color: 0x000000, alpha: 0.14 })); // shadow
  c.addChild(
    new Graphics()
      // desk top
      .roundRect(-60, -16, 120, 30, 4)
      .fill(top)
      .roundRect(-60, -16, 120, 7, 4)
      .fill({ color: 0xffffff, alpha: 0.16 })
      // legs
      .rect(-54, 14, 5, 10)
      .rect(49, 14, 5, 10)
      .fill(darken(top, 0.3)),
  );
  // dual monitors standing on the desk back edge
  for (const mx of [-26, 26]) {
    c.addChild(
      new Graphics()
        .roundRect(mx - 20, -34, 40, 22, 2)
        .fill(0x20242c)
        .roundRect(mx - 17, -31, 34, 16, 1)
        .fill(screen)
        .roundRect(mx - 17, -31, 34, 6, 1)
        .fill({ color: lighten(screen, 0.18), alpha: 0.5 })
        .rect(mx - 2, -12, 4, 5)
        .fill(0x20242c),
    );
  }
  // a graphics tablet + pen cup on the desktop
  c.addChild(
    new Graphics()
      .roundRect(-14, -8, 28, 18, 2)
      .fill(darken(steel, 0.1))
      .roundRect(-11, -5, 22, 12, 1)
      .fill({ color: lighten(steel, 0.1), alpha: 0.6 })
      .roundRect(20, -10, 7, 10, 2)
      .fill(mix(art.accent, 0x5a4a7a, 0.4))
      .rect(22, -16, 1.5, 7)
      .rect(24, -15, 1.5, 6)
      .fill(darken(art.accent, 0.1)),
  );
  return c;
}

/** A small side table with a steaming mug — the ideation-nook companion to the armchair. */
function sideTable(ctx: RoomContext): Container {
  const { art, mix, lighten, darken } = ctx;
  const c = new Container();
  const wood = mix(0x6f4a28, art.wall, 0.2);
  c.addChild(
    new Graphics()
      .ellipse(0, 10, 14, 5)
      .fill({ color: 0x000000, alpha: 0.14 })
      .ellipse(0, 0, 15, 8)
      .fill(wood)
      .ellipse(0, -2, 15, 8)
      .fill(lighten(wood, 0.1))
      .rect(-2, 4, 4, 10)
      .fill(darken(wood, 0.2))
      // mug + steam
      .roundRect(-5, -8, 9, 8, 2)
      .fill(lighten(art.accent, 0.2))
      .moveTo(0, -10)
      .bezierCurveTo(-3, -14, 3, -16, 0, -20)
      .stroke({ color: 0xffffff, width: 1.5, alpha: 0.3 }),
  );
  return c;
}

// Bespoke FURNITURE registered as editable scenery (the level editor re-renders/adds these). The pin
// wall is BACKDROP (a framed cork board fixed to the north wall) and is intentionally NOT registered.
registerScenery('design-desk', (art) => designDesk(synthCtx(art)), 'Design desk', '🛋️');
registerScenery('side-table', (art) => sideTable(synthCtx(art)), 'Side table', '🛋️');

const decorate: RoomDecorator = (ctx: RoomContext) => {
  const { zone, art, f } = ctx;
  const left = zone.x; // 1120
  const right = zone.x + zone.width; // 1560
  const top = zone.y; // 580
  const bottom = zone.y + zone.height; // 840

  // Hero desk group, shifted RIGHT of the west walk-in lane so the door band y[640..780] stays clear.
  const deskX = 1360; // desk + chair share this column (clear of the W door lane at x<~1215)
  const deskTopY = top + 90; // 670 — desk surface, monitors at its back edge facing down
  const chairY = top + 126; // 706 — designer's chair, pulled up below the desk

  // ── ONE neat RUG sized to the DESK GROUP only (not a centred room-blob): it underlaps the desk slab
  //    and the pulled-up chair, inset a touch on every side so the furniture sits cleanly on top of it.
  //    Centred between the desk (≈670) and the chair (≈706) on the deskX column. ───────────────────────
  place(ctx, f.rug(art, 150, 108), deskX, top + 110);

  // ── PIN WALL of concept art along the NORTH wall, centred over the desk (clear of the corners). ──
  place(ctx, pinWall(ctx, 200, 44), deskX, top + 24);

  // ── HERO: the DESIGN DESK with dual monitors, facing DOWN into the room. ──
  place(ctx, designDesk(ctx), deskX, deskTopY, 'design-desk');
  // SEAT — the designer's chair, pulled up below the desk, FACING UP toward the monitors.
  // Anchor: (1360,706) facing -π/2 → register as studio-desk-chair (kind 'chair') in world/seats.ts.
  place(ctx, f.deskChair(art, 'up'), deskX, chairY);

  // ── IDEATION NOOK in the SE corner: an armchair + a side table, framed by a bookshelf on the E wall. ──
  // SEAT — the reading armchair, back to the SE corner, opening UP/inward into the room.
  // Anchor: (1502,798) facing -π/2 → register as studio-nook-chair (kind 'chair') in world/seats.ts.
  place(ctx, f.armchair(art), right - 58, bottom - 42);
  // its companion side table with a steaming mug, just to the chair's left (toward the room).
  place(ctx, sideTable(ctx), right - 108, bottom - 40, 'side-table');

  // ── ART-BOOK SHELF on the EAST wall, anchoring the nook's reading corner (body ≥16px inside the wall:
  //    half-width 22, so centre at x=right-40=1520 keeps its right edge at 1542, 18px off the E wall). ──
  place(ctx, f.bookshelf(ctx.art), right - 40, bottom - 130);

  // ── A framed poster on the NORTH wall, in from the NW corner (balances the pin board). ──
  place(ctx, f.wallArt(art, 'poster'), left + 60, top + 24);

  // ── PLANTS: exactly TWO, deliberate, in opposite corners — NE (tall) and SW (leafy). ──────────────
  place(ctx, f.plant(art, 'tall'), right - 28, top + 42); // NE corner
  place(ctx, f.plant(art, 'leafy'), left + 40, bottom - 30); // SW corner

  // ── A floor LAMP warming the NW corner ABOVE the door band (its base + glow stay clear of y[640..780]). ──
  place(ctx, f.lamp(art), left + 44, top + 30);
};

export default decorate;
